import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle,
  ShieldStar,
  Users,
  Cpu,
  CodeSimple,
  Sparkle,
  MapTrifold,
  ChartLineUp,
  TreeStructure,
  Plug,
  Lightning,
  MicrophoneStage,
  UserCircleCheck,
  Bank,
  SquaresFour,
  Question,
} from "@phosphor-icons/react";
import { SITE, PRODUCTS, MANAGED_ADDONS, VERIFYPRO_CONNECTORS, VERIFYPRO_CAPABILITIES, DIGITAL_CHECKS } from "@/lib/site";
import Seo from "@/components/Seo";
import { PAGE_SEO, VERIFYPRO_FAQS, verifyProProductSchema, opsDeskProductSchema, faqSchema, howToSchema } from "@/lib/seo";

const ADDON_ICONS = { ShieldStar, Users, Cpu, CodeSimple };
const CAP_ICONS = { MapTrifold, ChartLineUp, TreeStructure, MicrophoneStage, UserCircleCheck, Bank, SquaresFour };

const StatusPill = ({ status }) => {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-[0.2em]">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Live
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-[0.2em]">
      Coming soon
    </span>
  );
};

const ServicesPage = () => {
  return (
    <div data-testid="services-page">
      <Seo
        page={PAGE_SEO.products}
        schemas={[
          verifyProProductSchema(),
          opsDeskProductSchema(),
          faqSchema(),
          howToSchema(),
        ]}
      />
      <p className="sr-only">{PAGE_SEO.products.h1Fact}</p>
      {/* HERO */}
      <section className="bg-[#fff6ec]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <h1
            data-testid="services-hero-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#1f2a44]"
          >
            The Infini10<br />
            <span className="text-[#fe4c1c] italic font-light">product suite.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-slate-600 leading-relaxed">
            Every product shares the same design language, the same data model and the same account. Start with the one you need today; the rest slot in as you grow.
          </p>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section id="products" className="py-24 lg:py-32 bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 space-y-8">
          {PRODUCTS.map((p, idx) => {
            const live = p.status === "live";
            return (
              <article
                key={p.slug}
                id={p.slug}
                data-testid={`product-${p.slug}`}
                className={`relative rounded-3xl border overflow-hidden scroll-mt-28 ${
                  live
                    ? "bg-[#1f2a44] text-white border-[#1f2a44]"
                    : "bg-white text-[#1f2a44] border-slate-200"
                }`}
              >
                {live && (
                  <div
                    aria-hidden
                    className="absolute -bottom-24 -right-24 h-[400px] w-[400px] rounded-full opacity-25"
                    style={{ background: "radial-gradient(closest-side, rgba(254,76,28,0.6), transparent 70%)" }}
                  />
                )}
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 lg:p-14 items-start">
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-4">
                      <span
                        className="inline-flex h-16 w-16 items-center justify-center rounded-2xl font-display text-3xl font-bold text-white shrink-0"
                        style={{ backgroundColor: p.color }}
                      >
                        {p.letter}
                      </span>
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h2 className={`font-display text-3xl lg:text-4xl font-bold ${live ? "text-white" : "text-[#1f2a44]"}`}>
                            {p.name}
                          </h2>
                          <StatusPill status={p.status} />
                        </div>
                        <p className={`text-sm mt-1 ${live ? "text-white/60" : "text-slate-500"}`}>
                          {p.tagline}
                        </p>
                      </div>
                    </div>

                    <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${live ? "text-white/80" : "text-slate-600"}`}>
                      {p.short}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                      {live ? (
                        <>
                          <a
                            href={p.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`product-cta-launch-${p.slug}`}
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors"
                          >
                            Launch {p.name} <ArrowUpRight size={16} weight="bold" />
                          </a>
                          <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                          >
                            Book a demo
                          </Link>
                        </>
                      ) : (
                        <Link
                          to="/contact"
                          data-testid={`product-cta-early-${p.slug}`}
                          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1f2a44] text-[#fe4c1c] text-sm font-semibold hover:bg-[#152038] transition-colors"
                        >
                          Get early access <ArrowRight size={16} weight="bold" />
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <p className={`text-xs uppercase tracking-[0.22em] font-bold ${live ? "text-[#fe4c1c]" : "text-slate-400"}`}>
                      Key capabilities
                    </p>
                    <ul className="mt-4 space-y-3">
                      {p.features.map((f) => (
                        <li
                          key={f}
                          className={`flex items-start gap-3 text-sm ${live ? "text-white/85" : "text-slate-700"}`}
                        >
                          <CheckCircle size={18} weight="fill" className={live ? "text-[#fe4c1c]" : "text-[#1f2a44]"} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ============ VERIFYPRO DEEP-DIVE: CONNECTORS + ADVANCED CAPABILITIES ============ */}
      <section id="verifypro-deep-dive" className="py-24 lg:py-32 bg-[#fff6ec] border-y border-[#1f2a44]/10 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
              VerifyPro · Connectors & Advanced Capabilities
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]">
              Zero-touch BGV,<br />end to end.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
              VerifyPro plugs directly into the HRMS / ATS you already run — so a new hire triggers a full verification without a single manual step. And when a case is in flight, our live command centre gives ops and leadership complete visibility.
            </p>
          </div>

          {/* CONNECTORS */}
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <Plug size={22} weight="duotone" className="text-[#fe4c1c]" />
              <h3 className="font-display text-2xl font-bold text-[#1f2a44]">Native connectors</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {VERIFYPRO_CONNECTORS.map((c) => (
                <div
                  key={c.name}
                  data-testid={`connector-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-2xl bg-white border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl hover:border-[#fe4c1c]/40 transition-all"
                >
                  {/* Connector "logo card" mockup */}
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex h-12 w-12 items-center justify-center rounded-xl font-display text-base font-bold text-white shrink-0"
                      style={{ backgroundColor: c.color }}
                    >
                      {c.letter}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg font-bold text-[#1f2a44] truncate">{c.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">{c.type} · Native</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Live
                    </span>
                    <Lightning size={14} weight="fill" className="text-[#fe4c1c]" />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-500 italic">
              Built more custom connectors on request — REST API + webhooks available on every plan.
            </p>

            {/* DIGITAL CHECKS */}
            <div className="mt-12">
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c] mb-4">Digital checks · India-native</p>
              <div className="flex flex-wrap gap-3">
                {DIGITAL_CHECKS.map((d) => (
                  <div key={d.name} data-testid={`digital-${d.name.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-4 py-3 hover:border-[#fe4c1c]/40 hover:shadow-md transition-all">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg font-display text-sm font-bold text-white" style={{ backgroundColor: d.color }}>
                      {d.letter}
                    </span>
                    <span className="font-display text-sm font-bold text-[#1f2a44]">{d.name}</span>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-600">
                      <span className="h-1 w-1 rounded-full bg-emerald-500" /> Live
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CAPABILITIES */}
          <div className="mt-20">
            <div className="flex items-center gap-3 mb-8">
              <Sparkle size={22} weight="fill" className="text-[#fe4c1c]" />
              <h3 className="font-display text-2xl font-bold text-[#1f2a44]">Command-centre capabilities</h3>
            </div>

            <div className="space-y-8">
              {VERIFYPRO_CAPABILITIES.map((cap, idx) => {
                const Icon = CAP_ICONS[cap.icon] || Sparkle;
                const reverse = idx % 2 === 1;
                return (
                  <div
                    key={cap.title}
                    data-testid={`capability-${cap.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-3xl bg-white border border-slate-200 overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                  >
                    {/* MOCKUP */}
                    <div className="lg:col-span-6 bg-[#0a1224] p-8 flex items-center justify-center min-h-[300px]">
                      {cap.icon === "MapTrifold" && <FieldAgentMap />}
                      {cap.icon === "ChartLineUp" && <CXODashboard />}
                      {cap.icon === "TreeStructure" && <TopologyMap />}
                      {cap.icon === "MicrophoneStage" && <MultimodalAgent />}
                      {cap.icon === "UserCircleCheck" && <SelfCheckIn />}
                      {cap.icon === "Bank" && <AssistedBFSI />}
                      {cap.icon === "SquaresFour" && <OneConsole />}
                    </div>
                    {/* COPY */}
                    <div className="lg:col-span-6 p-8 lg:p-10">
                      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fe4c1c]/10 text-[#fe4c1c] text-[10px] font-bold uppercase tracking-[0.22em]">
                        <Icon size={12} weight="duotone" /> {cap.tag}
                      </span>
                      <h4 className="mt-4 font-display text-2xl lg:text-3xl font-bold text-[#1f2a44] leading-tight">{cap.title}</h4>
                      <p className="mt-4 text-slate-600 leading-relaxed">{cap.desc}</p>
                      <ul className="mt-6 space-y-2.5">
                        {cap.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle size={16} weight="fill" className="text-[#fe4c1c] shrink-0 mt-0.5" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* MANAGED SERVICES (ADD-ON) */}
      <section id="managed-services" className="py-24 lg:py-32 bg-[#fff6ec] border-y border-[#1f2a44]/10 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
              Optional · Add-on services
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]">
              If you&apos;d rather<br />not run it yourself.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
              Our products are self-serve by design — but our team is available on an add-on basis for anyone who&apos;d prefer to outsource. Billed per case or per month, no long contracts.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MANAGED_ADDONS.map((a) => {
              const Icon = ADDON_ICONS[a.icon] || ShieldStar;
              return (
                <div
                  key={a.title}
                  data-testid={`services-addon-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-[#1f2a44]/30 hover:shadow-md transition-all"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c]/10 text-[#fe4c1c]">
                    <Icon size={20} weight="duotone" />
                  </span>
                  <h4 className="mt-5 font-display text-lg font-bold text-[#1f2a44]">{a.title}</h4>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                  <p className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#fe4c1c]">
                    Add-on · extra cost
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="managed-services-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1f2a44] text-[#fe4c1c] text-sm font-semibold hover:bg-[#152038] transition-colors"
            >
              Talk to our managed team <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ — AEO/GEO friendly with FAQPage schema */}
      <section id="verifypro-faq" className="py-24 lg:py-32 bg-white border-t border-slate-200 scroll-mt-28" data-testid="verifypro-faq-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fe4c1c]/10 text-[#fe4c1c] text-[10px] font-bold uppercase tracking-[0.22em]">
              <Question size={12} weight="fill" /> FAQ
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]">
              VerifyPro, answered.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Everything you (and your compliance team) want to know about our AI-powered Background Verification platform for Indian enterprises and BFSI.
            </p>
          </div>

          <div className="space-y-4">
            {VERIFYPRO_FAQS.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group rounded-2xl bg-[#fff6ec] border border-slate-200 hover:border-[#fe4c1c]/40 transition-colors overflow-hidden"
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <h3 className="font-display text-base sm:text-lg font-bold text-[#1f2a44] leading-snug">
                    {f.q}
                  </h3>
                  <span className="shrink-0 h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#fe4c1c] group-open:rotate-45 transition-transform">
                    <ArrowUpRight size={16} weight="bold" />
                  </span>
                </summary>
                <div className="px-6 pb-6 -mt-1 text-slate-700 leading-relaxed text-sm sm:text-base">
                  {f.a}
                </div>
              </details>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-[#1f2a44] text-white p-8 lg:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">Still curious?</p>
              <p className="mt-2 font-display text-2xl font-bold">Talk to a VerifyPro specialist.</p>
            </div>
            <Link
              to="/contact"
              data-testid="faq-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors shrink-0"
            >
              Book a demo <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-3xl overflow-hidden bg-[#1f2a44] text-white p-10 lg:p-16 relative">
            <div
              aria-hidden
              className="absolute -bottom-24 -right-24 h-[420px] w-[420px] rounded-full opacity-25"
              style={{ background: "radial-gradient(closest-side, rgba(254,76,28,0.6), transparent 70%)" }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05]">
                  Try our first product,<br />
                  <span className="text-[#fe4c1c]">on us.</span>
                </h2>
                <p className="mt-5 text-white/75 max-w-lg">
                  Fourteen days on VerifyPro. Ten cases. Zero risk. No credit card required.
                </p>
              </div>
              <div className="lg:justify-self-end flex flex-wrap gap-3">
                <a
                  href={SITE.verifyProUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-final-cta"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors"
                >
                  Launch VerifyPro <ArrowUpRight size={18} weight="bold" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ MOCKUP VISUALS ============

const FieldAgentMap = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Field Agents · Live</p>
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> 12 active
      </span>
    </div>
    {/* Stylised India-shape SVG with agent dots */}
    <div className="relative rounded-2xl bg-[#050815] border border-white/10 p-4 overflow-hidden">
      <svg viewBox="0 0 320 260" className="w-full h-auto">
        {/* very rough India-like outline (stylised) */}
        <path
          d="M120,20 L150,15 L180,25 L200,50 L220,80 L235,120 L230,155 L215,190 L180,215 L150,235 L130,220 L110,195 L95,165 L80,135 L75,105 L85,75 L100,45 Z"
          fill="none"
          stroke="rgba(254,76,28,0.3)"
          strokeWidth="1.5"
          strokeDasharray="3,3"
        />
        {/* Agent dots pulsing */}
        {[
          { cx: 130, cy: 55, delay: "0s" },
          { cx: 165, cy: 95, delay: "0.4s" },
          { cx: 195, cy: 130, delay: "0.8s" },
          { cx: 125, cy: 150, delay: "1.2s" },
          { cx: 175, cy: 175, delay: "0.2s" },
          { cx: 205, cy: 100, delay: "0.6s" },
        ].map((d, i) => (
          <g key={i}>
            <circle cx={d.cx} cy={d.cy} r="6" fill="#fe4c1c" opacity="0.3">
              <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" begin={d.delay} />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" begin={d.delay} />
            </circle>
            <circle cx={d.cx} cy={d.cy} r="3" fill="#fe4c1c" />
          </g>
        ))}
        <text x="130" y="70" fill="#fff" fontSize="8" opacity="0.6">Delhi</text>
        <text x="200" y="145" fill="#fff" fontSize="8" opacity="0.6">Blr</text>
      </svg>
      <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-white font-display font-bold">12</p>
          <p className="text-[9px] uppercase tracking-wider text-white/40">Active</p>
        </div>
        <div>
          <p className="text-white font-display font-bold">48</p>
          <p className="text-[9px] uppercase tracking-wider text-white/40">Cases today</p>
        </div>
        <div>
          <p className="text-emerald-400 font-display font-bold">98%</p>
          <p className="text-[9px] uppercase tracking-wider text-white/40">On-SLA</p>
        </div>
      </div>
    </div>
  </div>
);

const CXODashboard = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">CXO Dashboard · Q3</p>
      <span className="text-[10px] font-bold text-emerald-400">▲ 18%</span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/5 p-3 border border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-white/40">Cases MTD</p>
          <p className="text-white font-display text-2xl font-bold mt-1">1,284</p>
        </div>
        <div className="rounded-lg bg-white/5 p-3 border border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-white/40">Median TAT</p>
          <p className="text-[#fe4c1c] font-display text-2xl font-bold mt-1">4.2d</p>
        </div>
        <div className="rounded-lg bg-white/5 p-3 border border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-white/40">Discrepancy</p>
          <p className="text-amber-400 font-display text-2xl font-bold mt-1">3.4%</p>
        </div>
        <div className="rounded-lg bg-white/5 p-3 border border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-white/40">SLA breach</p>
          <p className="text-white font-display text-2xl font-bold mt-1">0.8%</p>
        </div>
      </div>
      {/* Bar chart */}
      <div className="mt-4 flex items-end gap-1.5 h-16">
        {[40, 55, 45, 65, 50, 78, 88, 72, 92].map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t ${i > 5 ? "bg-[#fe4c1c]" : "bg-white/20"}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  </div>
);

const TopologyMap = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Case VP-2026-042 · Topology</p>
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[#fe4c1c]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#fe4c1c] animate-pulse" /> In Progress
      </span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-5">
      <div className="flex items-center justify-between">
        {[
          { label: "Consent", state: "done" },
          { label: "Identity", state: "done" },
          { label: "Address", state: "active" },
          { label: "Education", state: "pending" },
          { label: "Report", state: "pending" },
        ].map((n, i, arr) => (
          <div key={n.label} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold ${
                  n.state === "done"
                    ? "bg-emerald-500 text-white"
                    : n.state === "active"
                    ? "bg-[#fe4c1c] text-white ring-4 ring-[#fe4c1c]/20"
                    : "bg-white/10 text-white/40"
                }`}
              >
                {n.state === "done" ? "✓" : i + 1}
              </span>
              <p className={`mt-2 text-[9px] font-bold ${n.state === "pending" ? "text-white/40" : "text-white"}`}>{n.label}</p>
            </div>
            {i < arr.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${n.state === "done" ? "bg-emerald-500" : "bg-white/10"}`} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 text-[11px] text-white/60">
        <p>Owner: <span className="text-white">Priya M.</span> · SLA: <span className="text-emerald-400">Within</span></p>
      </div>
    </div>
  </div>
);

const MultimodalAgent = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Executive Console · Live</p>
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#fe4c1c]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#fe4c1c] animate-pulse" /> Listening
      </span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-6 text-center">
      <div className="relative inline-flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-[#fe4c1c]/20 animate-ping" />
        <div className="absolute inset-2 rounded-full bg-[#fe4c1c]/30" />
        <div className="relative h-16 w-16 rounded-full bg-[#fe4c1c] flex items-center justify-center text-3xl">👋</div>
      </div>
      <p className="mt-6 text-white font-display text-lg">Say it. Wave it.</p>
      <p className="text-[#fe4c1c] font-display text-lg font-bold italic">See it verified.</p>
      <div className="mt-5 rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-left">
        <p className="text-[10px] uppercase tracking-wider text-white/40">You said</p>
        <p className="text-sm text-white mt-1">&ldquo;Show me high-risk cases in Bangalore this week&rdquo;</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 justify-center">
        {["Voice", "Gesture", "Privacy-first"].map((t) => (
          <span key={t} className="text-[9px] uppercase tracking-wider font-bold text-[#fe4c1c] border border-[#fe4c1c]/40 rounded-full px-2 py-0.5">{t}</span>
        ))}
      </div>
    </div>
  </div>
);

const SelfCheckIn = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Candidate · Self Check-in</p>
      <span className="text-[10px] font-bold text-emerald-400">Step 2 of 3</span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-5">
      {[
        { label: "Consent captured", done: true },
        { label: "Upload Aadhaar / PAN", done: true, current: true },
        { label: "eKYC verification", done: false },
      ].map((s, i) => (
        <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-3 mb-2 ${s.current ? "bg-[#fe4c1c]/10 border border-[#fe4c1c]/30" : "bg-white/5"}`}>
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${s.done ? "bg-emerald-500 text-white" : "bg-white/10 text-white/40"}`}>
            {s.done ? "✓" : i + 1}
          </span>
          <p className={`text-sm ${s.done ? "text-white" : "text-white/60"}`}>{s.label}</p>
        </div>
      ))}
      <div className="mt-3 flex flex-wrap gap-2">
        {["Aadhaar", "PAN", "DigiLocker"].map((c) => (
          <span key={c} className="text-[10px] font-bold uppercase text-white bg-white/10 rounded-full px-3 py-1">{c}</span>
        ))}
      </div>
    </div>
  </div>
);

const AssistedBFSI = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">Branch · Assisted Mode</p>
      <span className="text-[10px] font-bold text-emerald-400">Walk-in · 4 min</span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-5">
      <div className="flex items-center gap-3 pb-4 border-b border-white/10">
        <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-lg">🏦</div>
        <div className="flex-1">
          <p className="text-sm text-white font-semibold">Branch Associate</p>
          <p className="text-[10px] text-white/50">Assisting Rajesh K. · Walk-in customer</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { label: "Docs OK", value: "✓" },
          { label: "eKYC OK", value: "✓" },
          { label: "Report", value: "⋯" },
        ].map((s, i) => (
          <div key={i} className="rounded-lg bg-white/5 py-3">
            <p className="text-lg font-bold text-[#fe4c1c]">{s.value}</p>
            <p className="text-[9px] uppercase tracking-wider text-white/40 mt-1">{s.label}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-white/60 text-center">Nearly 1 in 3 candidates need help finishing checks.</p>
    </div>
  </div>
);

const OneConsole = () => (
  <div className="w-full max-w-md">
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">One Console · All Modules</p>
      <span className="text-[10px] font-bold text-[#fe4c1c]">Multi-tenant SaaS</span>
    </div>
    <div className="rounded-2xl bg-[#050815] border border-white/10 p-5">
      <div className="grid grid-cols-2 gap-2">
        {[
          { name: "HRMS Sync", icon: "🔗", active: true },
          { name: "Digital Checks", icon: "🪪", active: true },
          { name: "Field Agents", icon: "📍", active: true },
          { name: "AI Insights", icon: "✨", active: true },
          { name: "CXO View", icon: "📊", active: true },
          { name: "Reports", icon: "📄", active: true },
        ].map((m, i) => (
          <div key={i} className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-center gap-2">
            <span className="text-lg">{m.icon}</span>
            <p className="text-xs text-white font-semibold flex-1">{m.name}</p>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          </div>
        ))}
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-wider text-white/40 text-center">Masked storage · Role-based access · DPDP</p>
    </div>
  </div>
);

export default ServicesPage;
